import { css, html, LitElement, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

/** How long a failed action stays on screen before clearing itself. */
export const DISMISS_AFTER_MS = 8_000;

/**
 * The app's one way of telling someone that something failed.
 *
 * It hangs from the top-right of the content area, directly under the account
 * button and over the top of the sightings panel — the two places a person is
 * when an action of theirs fails. Feedback far from the control that produced
 * it is feedback nobody reads. It is positioned against `main` rather than the
 * page so it clears the header without anyone hardcoding the header's height,
 * and it stays out of the layout, so nothing reflows when a failure appears.
 *
 * Top-left holds `user-location-control` and bottom-right holds Sentry's
 * feedback button; this corner is free in both layouts.
 */
@customElement('error-toast')
export default class ErrorToast extends LitElement {
  static styles = css`
    :host {
      pointer-events: none;
      position: absolute;
      right: 1rem;
      top: 1rem;
      z-index: 2;
    }
    .toast {
      align-items: start;
      background: var(--color-chrome);
      border-left: 4px solid var(--color-error-border);
      border-radius: 4px;
      box-shadow: var(--shadow-float);
      color: white;
      display: flex;
      font-size: var(--fs-body2);
      gap: 0.5rem;
      max-width: min(24rem, calc(100vw - 2rem));
      padding: 0.625rem 0.75rem;
      pointer-events: auto;
    }
    .icon {
      color: var(--color-error-border);
      flex-shrink: 0;
      line-height: 1.4;
    }
    p {
      margin: 0;
      line-height: 1.4;
    }
    button {
      background: none;
      border: 0;
      color: inherit;
      cursor: pointer;
      flex-shrink: 0;
      font-size: var(--fs-body1);
      line-height: 1.4;
      opacity: 0.7;
      padding: 0;
    }
    button:hover {
      opacity: 1;
    }
  `;

  @state()
  private message: string | null = null;

  #timer?: ReturnType<typeof setTimeout>;

  /**
   * Show a failure. The newest one replaces the current one rather than
   * stacking — a pile of toasts over the map is worse than the latest fact
   * about what is broken.
   *
   * `persist` is for a condition that is still true after the toast would have
   * gone (sightings that never loaded), as opposed to an action that failed
   * once (a sign-in, a delete).
   */
  show(message: string, {persist = false}: {persist?: boolean} = {}) {
    clearTimeout(this.#timer);
    this.#timer = undefined;
    this.message = message;
    if (!persist)
      this.#timer = setTimeout(() => this.dismiss(), DISMISS_AFTER_MS);
  }

  dismiss = () => {
    clearTimeout(this.#timer);
    this.#timer = undefined;
    this.message = null;
  };

  disconnectedCallback(): void {
    super.disconnectedCallback();
    clearTimeout(this.#timer);
  }

  protected render() {
    if (!this.message) return nothing;
    return html`
      <div class="toast" role="status" aria-live="polite">
        <span class="icon" aria-hidden="true">&#9888;</span>
        <p>${this.message}</p>
        <button type="button" @click=${this.dismiss} aria-label="Dismiss">&#10005;</button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "error-toast": ErrorToast;
  }
}
